import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Nav } from '../components/Nav';
import { getAllJournalEntries } from '../utils/api';
import { DAYS, JOURNAL_PROMPTS_BY_DAY, C } from '../utils/constants';

export const MyJournal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDay, setOpenDay] = useState(null);

  useEffect(() => {
    getAllJournalEntries()
      .then(res => setEntries(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group entries by day
  const byDay = {};
  entries.forEach(e => {
    if (!byDay[e.day_num]) byDay[e.day_num] = [];
    byDay[e.day_num].push(e);
  });

  const daysWithEntries = DAYS.filter(d => byDay[d.num]?.length > 0);

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      <Nav user={user} onAdmin={() => navigate('/admin')} />

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '32px 16px 64px' }}>
        <div style={{ marginBottom: 28 }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: C.terracotta, fontSize: '0.9rem', padding: 0,
              fontFamily: "'Nunito', sans-serif", marginBottom: 16,
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: 'clamp(1.4rem, 4vw, 1.9rem)',
            letterSpacing: '0.02em',
            marginBottom: 6,
          }}>
            My Journal
          </h1>
          <p style={{ color: C.terracotta, fontSize: '0.95rem' }}>
            Your reflections across all 7 days
          </p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: C.terracotta, padding: 40 }}>Loading...</div>
        ) : daysWithEntries.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: 14, padding: '48px 28px',
            textAlign: 'center', border: `1.5px solid ${C.border}`,
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>📝</div>
            <h2 style={{
              fontFamily: "'Forum', serif", color: C.brown,
              fontSize: '1.3rem', marginBottom: 10,
            }}>
              Your journal starts here
            </h2>
            <p style={{ color: C.terracotta, fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 28, maxWidth: 360, margin: '0 auto 28px' }}>
              Your journalling responses will be recorded here as you move through the program — all in one place to look back on.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/day/1/video')}
            >
              Start Day 1 →
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {daysWithEntries.map(day => {
              const dayEntries = byDay[day.num] || [];
              const isOpen = openDay === day.num;
              const lastUpdated = dayEntries.reduce((latest, e) =>
                e.updated_at > latest ? e.updated_at : latest, '');

              return (
                <div
                  key={day.num}
                  style={{
                    background: '#fff',
                    borderRadius: 14,
                    border: `1.5px solid ${isOpen ? C.gold : C.border}`,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                    boxShadow: isOpen ? '0 4px 20px rgba(245,166,35,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  {/* Day header — clickable */}
                  <button
                    onClick={() => setOpenDay(isOpen ? null : day.num)}
                    style={{
                      width: '100%', background: 'none', border: 'none',
                      padding: '18px 20px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 14,
                      textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: "'Forum', serif", color: '#fff', fontWeight: 700, fontSize: '1rem',
                    }}>
                      {day.num}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: "'Forum', serif", fontSize: '0.72rem',
                        letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: C.terracotta, marginBottom: 2,
                      }}>
                        Day {day.num}
                      </div>
                      <div style={{
                        fontFamily: "'Forum', serif", fontSize: '1.05rem', color: C.brown,
                      }}>
                        {day.title}
                      </div>
                      {lastUpdated && (
                        <div style={{ fontSize: '0.75rem', color: C.terracotta, marginTop: 2 }}>
                          {new Date(lastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '1.1rem', color: C.gold,
                      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      flexShrink: 0,
                    }}>
                      ▾
                    </div>
                  </button>

                  {/* Entries */}
                  {isOpen && (
                    <div style={{ borderTop: `1px solid ${C.border}`, padding: '20px 20px 24px' }}>
                      {dayEntries.map(entry => {
                        const prompt = (JOURNAL_PROMPTS_BY_DAY[entry.day_num] || [])[entry.prompt_index];
                        if (!prompt || !entry.answer?.trim()) return null;
                        return (
                          <div key={entry.prompt_index} style={{ marginBottom: 24 }}>
                            <div style={{
                              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.1em',
                              textTransform: 'uppercase', color: C.gold, marginBottom: 6,
                            }}>
                              {prompt.label || `Prompt ${entry.prompt_index + 1}`}
                            </div>
                            <div style={{
                              fontSize: '0.88rem', color: C.terracotta,
                              fontStyle: 'italic', marginBottom: 10, lineHeight: 1.5,
                            }}>
                              {prompt.q}
                            </div>
                            <div style={{
                              background: C.veryLightBrown, borderRadius: 10,
                              padding: '14px 16px', color: C.text,
                              fontSize: '0.95rem', lineHeight: 1.7,
                              borderLeft: `3px solid ${C.gold}`,
                              whiteSpace: 'pre-wrap',
                            }}>
                              {entry.answer}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
